<script setup>
import {computed, inject, onMounted, provide, ref} from 'vue';
import {ClientJS} from 'clientjs';
import useConnector from "./ConnectorComposition";
import useNickname, {USE_NICKNAME_KEY} from "./NicknameComposition";
import RuleModal from "./RuleModal.vue";
import NicknameContainer from "./NicknameContainer.vue";
import ConfirmModal from "./ConfirmModal.vue";
import CommentLine from "./CommentLine.vue";
const siteUrl = document.querySelector('meta[name="site-url"]').content;
const connector = useConnector();
const nicknameComposition = useNickname();
provide(USE_NICKNAME_KEY, nicknameComposition);

// 一回に表示するコメント数
const NUMBER = 3;

// フィンガープリントを用意
const fingerprint = (new ClientJS()).getFingerprint();

// マウント元からコラムの ID を取得
const columnId = Number(inject('columnId'));

// マウント元からログインしているユーザ ID を取得(未ログインは 0)
const userId = Number(inject('userId'));

// 現行のコメント
// 例:
// [{"id":1,"column_id":2,"user_id":1,"user_nickname_id":1,"content":"hoge\r\nhoge\r\nhoge","delete_type":0,"created_at":null,"updated_at":null,"deleted_at":null,"user_nickname":"noji-a","user_link_id":"n1","birthday":"867682800","count_empathy":0,"public_children":[{"id":4,"user_id":1,"sex":2,"birthday":"2018-02-28T15:00:00.000000Z","created_at":"2023-03-06T02:56:28.000000Z","updated_at":"2023-04-27T04:46:17.000000Z","deleted_at":null},{"id":5,"user_id":1,"sex":1,"birthday":"2018-04-30T15:00:00.000000Z","created_at":"2023-04-27T04:46:17.000000Z","updated_at":"2023-04-27T04:46:17.000000Z","deleted_at":null},{"id":6,"user_id":1,"sex":1,"birthday":"2018-05-31T15:00:00.000000Z","created_at":"2023-04-27T04:47:48.000000Z","updated_at":"2023-04-27T04:47:48.000000Z","deleted_at":null},{"id":7,"user_id":1,"sex":2,"birthday":"2018-07-31T15:00:00.000000Z","created_at":"2023-04-27T04:50:31.000000Z","updated_at":"2023-04-27T04:50:31.000000Z","deleted_at":null}],"public_prefecture":"\u6771\u4eac\u90fd"}]
const comments = ref([]);

// 現行のコメント共感 ID
const empathy = ref([]);

// 現行のコメント違反報告 ID
const violations = ref([]);

// 利用規約モーダルの表示
const isShowRuleModal = ref(false);

// コメント入力内容
const content = ref('');

// 違反報告モーダルの表示対象 index
const violationModalIndex = ref(0);

// 0: 非表示、1: 違反報告済コメント、2: 違反報告解除コメント
const violationModalMode = ref(0);

// 削除モーダルの表示
const deleteModalIndex = ref(-1);

// 現在表示されているコメントの数
const visibleCommentNumber = ref(NUMBER);

//本文の文字数を正確に数える
// https://qiita.com/ka215/items/d059a78e29adef3978b5#6-%E3%83%9E%E3%83%AB%E3%83%81%E3%83%90%E3%82%A4%E3%83%88%E6%96%87%E5%AD%97%E3%81%AB%E5%AF%BE%E5%BF%9C%E3%81%97%E3%81%9F%E6%96%87%E5%AD%97%E5%88%97%E5%87%A6%E7%90%86
const contentLength = computed(() => {

  //指定された範囲にサロゲートペアが存在するかを boolean で返す
  //upper は 1 バイト目、lower に 2 バイト目を指定する
  //UTF の場合、通常は 2 バイトで一文字だが、サロゲートペアの場合は 4 バイトで一文字を表す
  const isSurrogatePair = (upper, lower) => 0xD800 <= upper && upper <= 0xDBFF && 0xDC00 <= lower && lower <= 0xDFFF;

  //最終的に返す文字数
  let result = 0;

  //サロゲートペアがあればそれを 2 文字ではなく 1 文字としてカウントしつつ文字数を返す
  for (let i = 0; i < content.value.length; i++,result++) {
    //サロゲートペアでない文字は一文字とカウントする
    if ( ! isSurrogatePair(content.value.charCodeAt(i), content.value.length > (i + 1) ? content.value.charCodeAt(i + 1) : 0)) {
      continue;
    }

    //サロゲートペアなら一文字ぶん多くカウントする
    i++;
  }

  //スペースと改行を文字として除外する
  for (let i = 0; i < content.value.length; i++) {
    //スペース or 改行でなければ何もしない
    if ( ! /\r\n|\r|\n| |　/.test(content.value.charAt(i))) {
      continue;
    }

    //文字カウントを減らす
    result--;
  }

  //結果文字列の数を返す
  return result;
});

// コメントの内容が適切か判定
const validContentLength = computed(() => contentLength.value > 0 && contentLength.value <= 500);

// 既にコメントしているか判定
const isCommented = computed(() => comments.value.some(comment => Number(comment.user_id) === userId));

// 表示されるコメント
const visibleComments = computed(() => comments.value.slice(0, visibleCommentNumber.value));

// コメントの初期化
const init = async () => {
  // コメントを取得
  const response = await connector.connectGet(`${siteUrl}column/get-comments/${columnId}?fingerprint=${fingerprint}`);

  // 取得できたらコメントを更新
  if (response !== null) {
    comments.value = response.comments;
    empathy.value = response.empathy;
    violations.value = response.violations;
  }
};

// コメントの投稿
const setComment = async () => {
  // ログインしていないなら何もしない
  if (userId === 0) {
    return;
  }

  // 投稿から FormData を生成
  const formData = new FormData();
  formData.append('user_nickname_id', nicknameComposition.state.nicknameId);
  formData.append('content', content.value);

  // 投稿
  const response = await connector.connectPost(`${siteUrl}column/set-comment/${columnId}`, formData);

  // 成功ならコメントを更新
  if (response !== null) {
    init();
  }
};

// コメントの削除
const deleteComment = async (index) => {
  // index が示すコメントのユーザ ID がログインユーザ ID と一致しないなら何もしない
  if (Number(comments.value[index].user_id) !== userId) {
    return;
  }

  // 削除
  const response = await connector.connectPost(`${siteUrl}column/delete-comment/${comments.value[index].id}`);

  // 成功ならコメントを更新
  if (response !== null) {
    deleteModalIndex.value = -2;
    init();
  }
};

// 違反報告
const setCommentViolation = async (index) => {
  console.log('setCommentViolation');
  // ログインしていないなら何もしない
  if (userId === 0) {
    console.log('setCommentViolation0');
    return;
  }

  // index が示すコメントのユーザ ID がログインユーザ ID と一致するなら何もしない
  if (Number(comments.value[index].user_id) === userId) {
    console.log('setCommentViolation1');
    return;
  }

  // 既に違反報告しているなら何もしない
  if (violations.value.some(violation => Number(violation) === Number(comments.value[index].id))) {
    console.log('setCommentViolation2');
    return;
  }

  // 違反報告
  const response = await connector.connectPost(`${siteUrl}column/set-comment-violation/${comments.value[index].id}`);

  // 成功ならコメントを更新
  if (response !== null) {
    init();
  }
};

// 違反報告削除
const deleteCommentViolation = async (index) => {
  // ログインしていないなら何もしない
  if (userId === 0) {
    return;
  }

  // index が示すコメントのユーザ ID がログインユーザ ID と一致するなら何もしない
  if (Number(comments.value[index].user_id) === userId) {
    return;
  }

  // 違反報告していないなら何もしない
  if ( ! violations.value.some(violation => Number(violation) === Number(comments.value[index].id))) {
    return;
  }

  // 違反報告削除
  const response = await connector.connectPost(`${siteUrl}column/delete-comment-violation/${comments.value[index].id}`);

  // 成功ならコメントを更新
  if (response !== null) {
    init();
  }
};

// 共感
const setCommentEmpathy = async (index) => {
  // index が示すコメントのユーザ ID がログインユーザ ID と一致するなら何もしない
  if (Number(comments.value[index].user_id) === userId) {
    return;
  }

  // 既に共感しているなら何もしない
  if (empathy.value.some(empathy => Number(empathy) === Number(comments.value[index].id))) {
    return;
  }

  // 共感
  const formData = new FormData();
  formData.append('fingerprint', fingerprint);
  const response = await connector.connectPost(`${siteUrl}column/set-comment-empathy/${comments.value[index].id}`, formData);

  // 成功ならコメントを更新
  if (response !== null) {
    init();
  }
};

// 共感削除
const deleteCommentEmpathy = async (index) => {
  // index が示すコメントのユーザ ID がログインユーザ ID と一致するなら何もしない
  if (Number(comments.value[index].user_id) === userId) {
    return;
  }

  // 共感していないなら何もしない
  if ( ! empathy.value.some(empathy => Number(empathy) === Number(comments.value[index].id))) {
    return;
  }

  // 共感削除
  const formData = new FormData();
  formData.append('fingerprint', fingerprint);
  const response = await connector.connectPost(`${siteUrl}column/delete-comment-empathy/${comments.value[index].id}`, formData);

  // 成功ならコメントを更新
  if (response !== null) {
    init();
  }
};

// 共感・解除
const requestEmpathy = async (payload) => {
  // 共感
  const response = await payload.checked ? setCommentEmpathy(payload.index) : deleteCommentEmpathy(payload.index);

  // 成功ならコメントを更新
  if (response !== null) {
    init();
  }
};

// 違反報告・解除
const requestViolation = async (payload) => {
  // 違反報告
  const response = await payload.checked ? setCommentViolation(payload.index) : deleteCommentViolation(payload.index);

  // 成功ならモーダルを表示・コメントを更新
  if (response !== null) {
    violationModalIndex.value = payload.index;
    violationModalMode.value = payload.checked ? 1 : 2;
    init();
  }
};

// 削除リクエスト
const requestDelete = (index) => {
  deleteModalIndex.value = index;
};

// 初期化
onMounted(() => {
  init();
});
</script>

<template>
  <div>
    <div
      v-if=" ! isCommented"
      class="gbMainInner"
    >
      <section id="commentPost">
        <h2 class="lyVerticalCnt lyMb16 lyPt8 lyPb8 lyPr4 lyPl4 pFont16 pFontBold pBorderBottom2pxSolidBlack">
          コメントを書く
          <span class="lyMl8 pFont10 pFontGray50 pFontMedium">利用規約を遵守の上、投稿お願いします</span>
        </h2>
        <div class="lyPt8 lyMb16">
          <ul class="lyMb8">
            <li class="lyVerticalStart pFont12 pFontMedium">
              <span class="lyBlock lyShrinkNone lyW16px lyMt2 lyMr2"><img
                :src="siteUrl+'images/iconViolationBlack.svg'"
                width="15"
                height="14"
                loading="lazy"
                alt=""
              ></span>
              他人への誹謗中傷や名誉毀損にあたる内容は禁止
            </li>
            <li class="lyVerticalStart pFont12 pFontMedium">
              <span class="lyBlock lyShrinkNone lyW16px lyMt2 lyMr2"><img
                :src="siteUrl+'images/iconViolationBlack.svg'"
                width="15"
                height="14"
                loading="lazy"
                alt=""
              ></span>
              他人に不快感を与えるコメントは禁止
            </li>
            <li class="lyVerticalStart pFont12 pFontMedium">
              <span class="lyBlock lyShrinkNone lyW16px lyMt2 lyMr2"><img
                :src="siteUrl+'images/iconViolationBlack.svg'"
                width="15"
                height="14"
                loading="lazy"
                alt=""
              ></span>
              個人情報の入力は禁止
            </li>
          </ul>
          <p class="lyPb8">
            <button
              class="lyVerticalCnt lyPt4 lyPr4 lyPb4 pFont12 pFontMedium"
              style="color:#36c"
              @click="isShowRuleModal = true"
            >
              詳しいルールを確認する
              <span class="lyBlock lyShrinkNone lyW16px lyMl2"><img
                :src="siteUrl+'images/iconDocument.svg'"
                width="15"
                height="14"
                loading="lazy"
                alt=""
              ></span>
            </button>
          </p>
        </div>
        <div :class="{'js-login-comment': userId === 0}">
          <div
            :style="userId === 0 ? 'pointer-events:none' : ''"
            class="lyMb4"
          >
            <NicknameContainer :is-login="userId !== 0" />
          </div>
          <div
            :style="userId === 0 ? 'pointer-events:none' : ''"
            class="lyMb16"
          >
            <div v-if="userId !== 0">
              <p
                v-for="error in connector.errors.value"
                class="lyMb8 pFont12 pFontMedium pFontRed lyHidden"
              >{{error}}</p>
            </div>
            <div class="lyOverFlewHidden pBorderRadius2px">
            <textarea
              v-model="content"
              class="js-flexibleTextArea lyW100 lyPd8 pBackColorWhite"
              placeholder="コメントを入力する"
              rows="10"
              style="height: 256px;"
            ></textarea>
              <div class="lyPd8 lyHorizontal pFont10 pBackColorWhite">
                <div :class="{pFontRed: ! validContentLength, pFontGreen: validContentLength, lyHorizontal: true}">
                  <p class="lyMr4">現在</p>
                  <p class="lyMr4">{{ new Intl.NumberFormat().format(contentLength) }}</p>
                  <p class="lyMr4">文字入力</p>
                </div>
                <p class="pFontGray30">※最大500文字入力可能</p>
              </div>
            </div>
          </div>
          <p
            :style="userId === 0 ? 'pointer-events:none' : ''"
            class="pageBoardPostBtn"
          >
            <button
              :class="{lyHvCnt: true, lyW100: true, lyPt12: true, lyPb12: true, lyTextCnt: true, pFont14: true, pFontBold: true, pFontWhite: true, pBackColorLightBlue: true, pBorderRadius2px: true, pHoverSlideChange: true, isDisabled: ! validContentLength || nicknameComposition.state.nicknameId < -1}"
              type="submit"
              @click="setComment"
            >コメント送信</button>
          </p>
        </div>
      </section>
    </div>
    <div class="gbMainInner">
      <section id="comment">
        <h2 class="lyMb16 lyPt8 lyPb8 lyPr4 lyPl4 pFont16 pFontBold pBorderBottom2pxSolidBlack">コメント（{{new Intl.NumberFormat().format(comments.length)}}）</h2>
        <div v-if="comments.length > 0">
          <CommentLine
            v-for="(comment, index) in visibleComments"
            :key="comment.id"
            :violations="violations"
            :empathy="empathy"
            :index="index"
            :comment="comment"
            :is-self="Number(comment.user_id) === userId"
            :user-id="userId"
            @set-violation="requestViolation"
            @set-empathy="requestEmpathy"
            @set-delete="requestDelete"
          />
          <p
            v-if="visibleCommentNumber < comments.length"
            class="lyMaxW400px lyBlockCnt"
          >
            <button
              type="button"
              class="lyHvCnt lyW100 lyPt12 lyPb12 lyTextCnt pFont14 pFontBold pFontBlack pBackColorGray4 pBorder1pxSolidBlack pBorderRadius2px"
              @click="visibleCommentNumber += NUMBER"
            >
              もっと見る
              <span class="lyBlock lyW16px lyMl8">
									<img
                    :src="siteUrl+'images/btnIconDownArrowBlack.svg'"
                    width="16"
                    height="16"
                    loading="lazy"
                    alt=""
                  >
								</span>
            </button>
          </p>
        </div>
        <p
          v-else
          class="lyPb24 lyPcPd16 pFont14 pFontBold"
        >コメントお待ちしております。</p>
      </section>
    </div>
    <teleport to="body">
      <RuleModal
        :is-show="isShowRuleModal"
        @request-close="isShowRuleModal = false"
      />
    </teleport>
    <teleport to="body">
      <ConfirmModal
        :is-show="violationModalMode === 1"
        title-text="違反報告"
        submit-text="違反報告を取り消す"
        @request-close="violationModalMode = false"
        @request-submit="requestViolation({index: violationModalIndex, checked: false})"
      >
        <p class="lyMb16 pFont14">違反報告ありがとうございます。<br>利用規約に照らし合わせて精査いたします。</p>
        <p class="lyMb16 pFont14">※もう一度「違反報告ボタン」をクリックすると取り消しができます。</p>
      </ConfirmModal>
    </teleport>
    <teleport to="body">
      <ConfirmModal
        :is-show="violationModalMode === 2"
        title-text="違反報告取り消し"
        submit-text=""
        @request-close="violationModalMode = false"
        @request-submit=""
      >
        <p class="lyMb16 pFont14">違反報告は取り消されました。</p>
      </ConfirmModal>
    </teleport>
    <teleport to="body">
      <ConfirmModal
        :is-show="deleteModalIndex > -1"
        title-text="コメント削除"
        submit-text="削除する"
        @request-close="deleteModalIndex = -1"
        @request-submit="deleteComment(deleteModalIndex)"
      >
        <p class="lyMb16 pFont14">投稿したコメントを削除します。<br>よろしいですか？</p>
      </ConfirmModal>
    </teleport>
    <teleport to="body">
      <ConfirmModal
        :is-show="deleteModalIndex === 2"
        title-text="コメント削除完了"
        submit-text=""
        @request-close="deleteModalIndex = -1"
        @request-submit=""
      >
        <p class="lyMb16 pFont14">投稿したコメントを削除しました。</p>
      </ConfirmModal>
    </teleport>
  </div>
</template>
